import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { withRouter, type RouteComponentProps } from "react-router-dom";
import { type Dispatch, type UnknownAction } from "redux";

import { listAllInfoHubDashboard, listAllInfoHubSubjects } from "../../../../actions";
import { AddNewContentButton, onClickAddNewContent } from "../AddNewContentButton";
import { Dashboard } from "../Dashboard";
import { Filters, type SubjectFilterValues } from "../Filters";
import { SubjectsDataTable } from "../SubjectsDataTable";
import { buildSubjectParams, DEFAULT_SUBJECT_FILTERS } from "../../utils";
import type {
  EntityId,
  InfoHubDashboardData,
  InfoHubSubjectRow,
  SubjectActions,
  SubjectTopic,
} from "../../types";
import type { CountryOption } from "../SubjectsDataTable/columns";

interface CountryObject {
  id: EntityId;
  name: string;
}

interface OwnProps extends RouteComponentProps {
  country: CountryObject;
  topics: SubjectTopic[];
  types: { label: string; value: string }[];
  countries: CountryOption[];
  setViewModalOptions: (options: { isOpen: boolean; id: EntityId }) => void;
  setConfirmModalOptions: (options: { isOpen: boolean; id: EntityId; type: string }) => void;
}

interface StateProps {
  infoHubSubjects: InfoHubSubjectRow[];
  isFetching: boolean;
  dashboardData?: InfoHubDashboardData;
  isDashboardFetching: boolean;
}

interface DispatchProps {
  listAllInfoHubSubjects: (filters: Record<string, unknown>) => void;
  listAllInfoHubDashboard: (filters: Record<string, unknown>) => void;
}

type AllCountriesTableProps = OwnProps & StateProps & DispatchProps;

export const AllCountriesTable = ({
  country,
  topics,
  types,
  countries,
  history,
  setViewModalOptions,
  setConfirmModalOptions,
  infoHubSubjects,
  isFetching,
  dashboardData,
  isDashboardFetching,
  listAllInfoHubSubjects,
  listAllInfoHubDashboard,
}: AllCountriesTableProps) => {
  const [filters, setFilters] = useState<SubjectFilterValues>(DEFAULT_SUBJECT_FILTERS);

  useEffect(() => {
    listAllInfoHubDashboard({ exclude_country: country.id });
  }, []);

  useEffect(() => {
    listAllInfoHubSubjects(buildSubjectParams(filters, { exclude_country: country.id }));
  }, [filters]);

  const actions: SubjectActions = useMemo(
    () => ({
      view: id => setViewModalOptions({ isOpen: true, id }),
      copy: id => setConfirmModalOptions({ isOpen: true, id, type: "copy" }),
      edit: id => history.push(`infohub/newContent/${id}`),
      publish: id => setViewModalOptions({ isOpen: true, id }),
    }),
    [history, setViewModalOptions, setConfirmModalOptions]
  );

  const topicOptions = useMemo(
    () => topics.map(topic => ({ value: topic.id, label: topic.name })),
    [topics]
  );

  return (
    <>
      <Dashboard
        data={dashboardData}
        isFetching={isDashboardFetching}
        onNewMessage={contentType => onClickAddNewContent(history, contentType)}
      />
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <Filters
          filters={filters}
          onChange={patch => setFilters(current => ({ ...current, ...patch }))}
          onClear={() => setFilters(DEFAULT_SUBJECT_FILTERS)}
          types={types}
          topics={topicOptions}
        />
        <AddNewContentButton history={history} />
      </div>
      <SubjectsDataTable
        data={infoHubSubjects}
        isFetching={isFetching}
        actions={actions}
        isAllCountries
        countries={countries}
      />
    </>
  );
};

interface RequestState<T> {
  isFetching: boolean;
  data: T;
}

interface RootState {
  listAllInfoHubSubjects: RequestState<{ results?: InfoHubSubjectRow[] }>;
  listAllInfoHubDashboard: RequestState<InfoHubDashboardData>;
}

export const mapStateToProps = (state: RootState): StateProps => ({
  infoHubSubjects: state.listAllInfoHubSubjects.data.results ?? [],
  isFetching: state.listAllInfoHubSubjects.isFetching,
  dashboardData: state.listAllInfoHubDashboard.data,
  isDashboardFetching: state.listAllInfoHubDashboard.isFetching,
});

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  listAllInfoHubSubjects: filters => dispatchAction(dispatch, listAllInfoHubSubjects(filters)),
  listAllInfoHubDashboard: filters => dispatchAction(dispatch, listAllInfoHubDashboard(filters)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(AllCountriesTable));
